import React, { useState, useRef, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { draftStory } from '../lib/claude'
import RichEditor, { applyFormatToEditor } from '../components/RichEditor'
import { smartQuotes } from '../lib/typography'
import ImagePickerOverlay from './ImagePickerOverlay'
import { resizeImage } from '../lib/images'

export default function IssueEditor({ story: initialStory, onClose }) {
  const [story, setStory] = useState(initialStory)
  const [text, setText] = useState(initialStory?.edited_text || initialStory?.draft_text || '')
  const [headline, setHeadline] = useState(initialStory?.headline || '')
  const [slug, setSlug] = useState(initialStory?.sport_slug || '')
  const [imageUrl, setImageUrl] = useState(initialStory?.image_url || '')
  const [imageCaption, setImageCaption] = useState(initialStory?.image_caption || '')
  const [imageCredit, setImageCredit] = useState(initialStory?.image_credit || '')
  const [drafting, setDrafting] = useState(false)
  const [saving, setSaving] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [submittingFeedback, setSubmittingFeedback] = useState(false)
  const autosaveTimer = useRef(null)
  const richEditorRef = useRef(null)
  const [showImagePicker, setShowImagePicker] = useState(false)

  // Build current state object for passing back on close
  function currentState() {
    return { ...story, edited_text: text, headline, sport_slug: slug, image_url: imageUrl, image_caption: imageCaption, image_credit: imageCredit }
  }

  const autosave = useCallback((newText, newHeadline, newSlug, newImageUrl, newImageCaption, newImageCredit) => {
    if (autosaveTimer.current) clearTimeout(autosaveTimer.current)
    autosaveTimer.current = setTimeout(async () => {
      if (!story) return
      setSaving(true)
      const updates = {
        edited_text: newText,
        headline: smartQuotes(newHeadline),
        sport_slug: newSlug,
        image_url: newImageUrl,
        image_caption: smartQuotes(newImageCaption),
        image_credit: smartQuotes(newImageCredit),
        status: 'edited',
        updated_at: new Date().toISOString()
      }
      await supabase.from('stories').update(updates).eq('id', story.id)
      setStory(prev => ({ ...prev, ...updates }))
      setSaving(false)
    }, 1200)
  }, [story])

  function handleSlugChange(e) {
    const val = e.target.value.toUpperCase()
    setSlug(val)
    autosave(text, headline, val, imageUrl, imageCaption, imageCredit)
  }

  function handleHeadlineChange(e) {
    const val = e.target.value
    setHeadline(val)
    autosave(text, val, slug, imageUrl, imageCaption, imageCredit)
  }

  function handleImageUrlChange(e) {
    const val = e.target.value
    setImageUrl(val)
    autosave(text, headline, slug, val, imageCaption, imageCredit)
  }

  function handleImageUrlPaste(e) {
    const pasted = e.clipboardData?.getData('text') || ''
    try {
      const payload = JSON.parse(pasted)
      if (payload.url && typeof payload.url === 'string' && payload.url.startsWith('http')) {
        e.preventDefault()
        const newCaption = payload.caption || ''
        const newCredit = payload.credit || ''
        setImageUrl(payload.url)
        setImageCaption(newCaption)
        setImageCredit(newCredit)
        autosave(text, headline, slug, payload.url, newCaption, newCredit)
      }
    } catch { /* plain URL paste — do nothing */ }
  }

  function handleImageCaptionChange(e) {
    const val = e.target.value
    setImageCaption(val)
    autosave(text, headline, slug, imageUrl, val, imageCredit)
  }

  function handleImageCreditChange(e) {
    const val = e.target.value
    setImageCredit(val)
    autosave(text, headline, slug, imageUrl, imageCaption, val)
  }

  function removeImage() {
    setImageUrl('')
    setImageCaption('')
    setImageCredit('')
    autosave(text, headline, slug, '', '', '')
  }

  async function handleImageUpload(e) {
    const file = e.target.files?.[0]
    if (!file || !story) return
    setUploading(true)
    const resized = await resizeImage(file)
    const ext = file.name.split('.').pop()
    const filename = story.id + '-' + Date.now() + '.' + ext
    const { error } = await supabase.storage.from('story-images').upload(filename, resized, { upsert: true })
    if (error) { console.error('Upload error:', error); setUploading(false); return }
    const { data: { publicUrl } } = supabase.storage.from('story-images').getPublicUrl(filename)
    await supabase.from('image_assets').insert({ filename, public_url: publicUrl, caption: '', credit: '' })
    setImageUrl(publicUrl)
    autosave(text, headline, slug, publicUrl, imageCaption, imageCredit)
    setUploading(false)
  }

  async function handleDraft() {
    if (!story) return
    setDrafting(true)
    const angleObj = story.proposed_angles && story.proposed_angles.find(a =>
      story.angle && story.angle.startsWith(a.label)
    )
    const angleToUse = angleObj || { label: 'Direct', description: story.angle || 'Cover the story directly' }
    const draftedText = await draftStory(story, angleToUse)
    setText(draftedText)
    setDrafting(false)
    await supabase.from('stories').update({
      draft_text: draftedText, edited_text: draftedText, status: 'drafted',
      updated_at: new Date().toISOString()
    }).eq('id', story.id)
    setStory(prev => ({ ...prev, draft_text: draftedText, edited_text: draftedText, status: 'drafted' }))
  }

  async function handleFeedbackSubmit() {
    if (!feedback.trim() || !text) return
    setSubmittingFeedback(true)
    await supabase.from('stories').update({
      search_queries: [...(story.search_queries || []), { feedback, timestamp: new Date().toISOString() }],
      updated_at: new Date().toISOString()
    }).eq('id', story.id)
    const prompt = 'Here is a story draft:\n\n' + text + '\n\nEditor feedback: ' + feedback + '\n\nPlease revise. Return only the revised story text.'
    const revised = await draftStory({ ...story, angle: prompt }, { label: 'Revision', description: feedback })
    setText(revised)
    await supabase.from('stories').update({ edited_text: revised, status: 'edited', updated_at: new Date().toISOString() }).eq('id', story.id)
    setFeedback('')
    setSubmittingFeedback(false)
  }

  function applyFormat(tag) {
    applyFormatToEditor(richEditorRef, tag)
  }

  const wordCount = text.trim().split(/\s+/).filter(Boolean).length

  return (
    <div className="modal-overlay">
      <div className="editor-modal" onClick={e => e.stopPropagation()}>
        <div className="editor-modal-header">
          <div className="editor-modal-meta">
            <input className="editor-slug-input" value={slug} onChange={handleSlugChange} placeholder="SPORT SLUG" />
            <textarea className="editor-headline-input" value={headline} onChange={handleHeadlineChange}
              placeholder="Headline" rows={1} />
            <p className="editor-angle">{story?.angle}</p>
          </div>
          <div className="editor-modal-actions">
            {saving && <span className="autosave-indicator">Saving…</span>}
            {!story?.draft_text && (
              <button className="btn-primary" onClick={handleDraft} disabled={drafting}>
                {drafting ? 'Drafting…' : '✍️ Draft with AI'}
              </button>
            )}
            {story?.draft_text && (
              <button className="btn-secondary" onClick={handleDraft} disabled={drafting}>
                {drafting ? 'Re-drafting…' : '↺ Re-draft'}
              </button>
            )}
            <button className="btn-primary" onClick={() => onClose(currentState())}>Save & close</button>
          </div>
        </div>

        <div className="editor-modal-body">
          {drafting && (
            <div className="drafting-state">
              <div className="loading-spinner" />
              <p>Drafting with Claude…</p>
            </div>
          )}

          {!drafting && (
            <>
              {/* Image */}
              <div className="editor-image-section">
                <label className="editor-label">Image</label>
                <div className="image-row">
                  <input className="editor-input" value={imageUrl}
                    onChange={handleImageUrlChange} onPaste={handleImageUrlPaste}
                    placeholder="https://… or paste from Image Manager" style={{ flex: 1 }} />
                  <label className="upload-btn" title="Upload image">
                    {uploading ? '…' : '⬆'}
                    <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} disabled={uploading} />
                  </label>
                  <button className="upload-btn" title="Image Manager"
                    onClick={() => setShowImagePicker(true)}>⊞</button>
                  {imageUrl && (
                    <>
                      <img src={imageUrl} alt="preview" className="image-preview" />
                      <button className="image-delete-btn" onClick={removeImage} title="Remove image">✕</button>
                    </>
                  )}
                </div>
                {showImagePicker && (
                  <ImagePickerOverlay
                    onSelect={image => {
                      setImageUrl(image.public_url)
                      setImageCaption(image.caption || '')
                      setImageCredit(image.credit || '')
                      autosave(text, headline, slug, image.public_url, image.caption || '', image.credit || '')
                      setShowImagePicker(false)
                    }}
                    onClose={() => setShowImagePicker(false)}
                  />
                )}
                <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                  <input className="editor-input" value={imageCaption}
                    onChange={handleImageCaptionChange} placeholder="Caption" style={{ flex: 1 }} />
                  <input className="editor-input" value={imageCredit}
                    onChange={handleImageCreditChange} placeholder="Credit" style={{ flex: 1 }} />
                </div>
              </div>

              {/* Toolbar */}
              <div className="editor-toolbar">
                <button className="toolbar-btn" onClick={() => applyFormat('bold')} title="Bold (⌘B)"><strong>B</strong></button>
                <button className="toolbar-btn" onClick={() => applyFormat('italic')} title="Italic (⌘I)"><em>I</em></button>
                <button className="toolbar-btn" onClick={() => applyFormat('link')} title="Link (⌘K)">⇗</button>
                <span className="word-count">{wordCount} words</span>
              </div>

              {/* Text */}
              <RichEditor
                ref={richEditorRef}
                value={text}
                onChange={val => { setText(val); autosave(val, headline, slug, imageUrl, imageCaption, imageCredit) }}
                placeholder="Draft will appear here after AI drafting, or type directly…"
                className="rich-editor"
              />

              {/* Feedback */}
              <div className="editor-feedback-section">
                <label className="editor-label">Feedback for Claude</label>
                <div className="feedback-row">
                  <textarea className="editor-input feedback-input" value={feedback}
                    onChange={e => setFeedback(e.target.value)} rows={2} placeholder="" />
                  <button className="btn-primary" onClick={handleFeedbackSubmit}
                    disabled={submittingFeedback || !feedback.trim() || !text}>
                    {submittingFeedback ? '…' : 'Submit'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
